import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { IMCMapView } from '../../../classes/mcMapView';
import { environment } from '../../../environment';
import { ESRI } from '../../../esri-loader-util';
import { BooleanConverter, InputConverter, NumberConverter } from '../../../inputConverter';
import { GisToolsService } from '../../../services/gis-tools.service';
import { IrowService } from '../../../services/irow.service';
import { BaseComponent } from '../../base.component';

import { IrowPlans } from './irow.model';

let Graphic: __esri.GraphicConstructor;
let Point: __esri.PointConstructor;
let SpatialReference: __esri.SpatialReferenceConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-irow-ng',
    styleUrls: ['../../sharedStyles/base.scss', './irow.component.scss'],
    templateUrl: './irow.component.html'
})
export class IrowComponent extends BaseComponent implements OnInit {
    private _active: boolean = false;
    @Input()
    set active(value: boolean) {
        this._active = BooleanConverter(value);
        this._active ? this.activate() : this.deactivate();
        this.activeChange.emit(this._active);
    }

    _centerMapZoomLevel = 10;
    @Input()
    set centerMapZoomLevel(value: number) {
        this._centerMapZoomLevel = NumberConverter(value);
    }
    get centerMapZoomLevel() {
        return this._centerMapZoomLevel;
    }

    _enableAutoZoomLevel = true;
    @Input()
    set enableAutoZoomLevel(value: boolean) {
        this._enableAutoZoomLevel = BooleanConverter(value);
    }
    get enableAutoZoomLevel() {
        return this._enableAutoZoomLevel;
    }
    pointSymbol: __esri.symbols.SimpleMarkerSymbol;
    _moveMapOnClick = true;
    @Input()
    set moveMapOnClick(value: boolean) {
        this._moveMapOnClick = BooleanConverter(value);
    }

    @Output() readonly activeChange: EventEmitter<boolean> = new EventEmitter();

    mapRef: IMCMapView;
    selectedPoint: __esri.geometry.Point;
    mapClickListener: IHandle;
    showSelect: boolean = false;
    // Irow Properties
    irowPlans: IrowPlans;
    plansExist: boolean = false;
    documentUrl: string = '';
    public options: any = {
        crsTolerance: 24
    };
    centerPoint: __esri.geometry.Point;
    pointGraphic: __esri.Graphic;
    @Input()
    public county: string = '';

    @Input()
    public route: string = '';

    public _segment: string = '';
    @Input()
    set segment(segment) {
        this._segment = segment;
        this.loadIrowSegment();
        this.changeDetectorRef.detectChanges();
    }
    get segment() {
        return this._segment;
    }

    @Input()
    @InputConverter(NumberConverter)
    public lat: number;

    @Input()
    @InputConverter(NumberConverter)
    public long: number;

    @Input()
    public tolerance: number = 100;

    //usecrsforirowbot checks if county, route and segment is provided as input for IROW bot. crs is county, route,segment.
    _usecrsforirowbot: boolean = false;
    @Input()
    set usecrsforirowbot(value) {
        this._usecrsforirowbot = value;
    }
    get usecrsforirowbot() {
        return this._usecrsforirowbot;
    }

    constructor(
        _vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly irowService: IrowService,
        private readonly gisToolsService: GisToolsService
    ) {
        super(_vcr);
        this.registerEvent('activeChange', this.activeChange);
    }

    async ngOnInit() {
        try {
            if (!this._usecrsforirowbot) {
                this.mapRef = await this.setupMapViewRef(this.mapRef);
            }
        } catch (err) {
            this.readyProblem(err);
        } finally {
            this.isReady();
        }
    }

    loadIrowSegment() {
        if (this.usecrsforirowbot) {
            this.getSegmentDataForIROW();
            this.changeDetectorRef.detectChanges();
        }
    }

    async getSegmentDataForIROW() {
        this.getSegmentData();
        this.showSelect = true;
    }
    /**
     * Make the tool active/shown.
     */
    private async activate() {
        await this.loadESRIClasses();
        if (!this.mapClickListener) {
            this.mapClickListener = this.mapRef.on('click', (evt) => this.handleMapClick(evt));
        }
    }

    /**
     * Make the tool inactive/hidden.
     */
    private deactivate(): void {
        if (this.mapClickListener) {
            this.mapClickListener.remove();
            this.mapClickListener = null;
            this.mapRef.graphics.remove(this.pointGraphic);
        }
    }

    async loadESRIClasses() {
        if (!Point || !Graphic || !SpatialReference) {
            [
                Point,
                Graphic,
                SpatialReference
            ] = await Promise.all([
                ESRI.Point,
                ESRI.Graphic,
                ESRI.SpatialReference
            ]);
        }
    }

    private async handleMapClick(evt) {
        if (evt.mapPoint) {
            this.selectedPoint = evt.mapPoint;
            this.lat = this.selectedPoint.latitude;
            this.long = this.selectedPoint.longitude;
            await this.getCSR(this.lat, this.long);
            await this.getSegmentData();
            this.addPoint(this.selectedPoint.longitude, this.selectedPoint.latitude);
            this.showSelect = true;
            this.changeDetectorRef.detectChanges();
        }
    }

    private async getSegmentData() {
        if (this.segment) {
            const irowPlans = await Promise.all([
                this.irowService.getPlans(this.county, this.route, this.segment)
            ]);
            this.documentUrl = environment.apiBaseUrl + '/irow/planDocument';
            this.irowPlans = irowPlans[0];
            if (this.irowPlans[0] != undefined) {
                this.plansExist = true;
            } else {
                this.plansExist = false;
            }
        }
        this.changeDetectorRef.detectChanges();
    }
    async getCSR(lat, long) {
        let segments: any;
        segments = await this.gisToolsService.getSegmentsNearLatLong(lat, long, this.tolerance);
        if (segments.length) {
            this.county = segments[0].countyCode;
            this.segment = segments[0].segmentNumber;
            this.route = segments[0].stateRouteNumber;
        }

    }

    private addPoint(long: number, lat: number) {
        if (this.mapRef) {
            if (this.pointGraphic) {
                this.mapRef.graphics.remove(this.pointGraphic);
            }
            const point = new Point({
                longitude: parseFloat(<any>long),
                latitude: parseFloat(<any>lat)
            });
            this.centerPoint = point;
            this.pointGraphic = new Graphic({
                geometry: point as __esri.GeometryProperties,
                symbol: this.pointSymbol as __esri.SymbolProperties
            });

            this.mapRef.graphics.add(this.pointGraphic);

            if (this._moveMapOnClick) {
                if (this.mapRef.zoom < this.centerMapZoomLevel && this.enableAutoZoomLevel) {
                    this.mapRef.goTo({
                        center: this.centerPoint,
                        zoom: this.centerMapZoomLevel
                    });
                } else {
                    this.mapRef.goTo({
                        center: this.centerPoint
                    });
                }
            }

            this.changeDetectorRef.detectChanges();
        }
    }
}
